import { useEffect, useState } from 'react';
import {
  postLoginCredentials,
  postQrPincode,
  postPhonePincode,
  contactUser,
  getResetPasswordRequest,
  postResetPassword
} from './api';
import { navigateToApp, loadify, throwOnServerError, routeTo } from './utils';
import { LOGIN_ROUTES, MFA_SESSION_KEYS } from 'pages/auth/constants';
import { SignInCredentials, SignInResponse, UseAuthType } from '../types';
import { useNavigate } from 'react-router-dom';
import { SignInResponseCode, navigateToApplication } from '../useAuthSass/utils';

export default function useAuthOnPrem(): UseAuthType {
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState<any>();
  const [isLoginOk, setIsLoginOk] = useState(false);
  const [error, setError] = useState<any>('');
  const [isSuccess, setSuccess] = useState(false);
  const navigate = useNavigate();

  const shouldRouteToPhonePin = (data: any) => data.phoneVerificationDto?.is_phone_pin_required;
  const shouldRouteToQrPin = (data: any) => data.mfaDto?.is_required;
  const shouldRouteToPasswordExpired = (data: any) => data.is_password_expired;
  const shouldRouteToMfa = (data: any) =>
    data.success && !data.signInDto && !data.is_error_msg && !data.is_password_expired && !!data.token && !data.mfaDto;

  const clearError = () => {
    setError('');
  };

  const login = async ({ email, password }: SignInCredentials) => {
    try {
      setError('');
      const postLoginCredentialsfy = loadify(postLoginCredentials, setLoading);
      const responseData = await postLoginCredentialsfy(email, password);
      setIsLoginOk(true);
      throwOnServerError(responseData);
      setResponseData(responseData);
    } catch (error) {
      setIsLoginOk(false);
      setLoading(false);
      if (!password) {
        throw error;
      }
      setError(error);
    }
  };

  useEffect(() => {
    if (isLoginOk) {
      if (responseData) {
        if (responseData.signInDto) {
          handleSignIn(responseData.signInDto);
        } else if (shouldRouteToPasswordExpired(responseData)) {
          routeTo(LOGIN_ROUTES.PASSWORD_EXPIRED, responseData, navigate);
        } else if (shouldRouteToPhonePin(responseData)) {
          routeTo(LOGIN_ROUTES.PHONE_VERIFICATION, responseData, navigate);
        } else if (shouldRouteToQrPin(responseData)) {
          routeTo(LOGIN_ROUTES.QR_VERIFICATION, responseData, navigate);
        } else if (shouldRouteToMfa(responseData)) {
          navigateToApp(responseData, navigate);
        } else if (!responseData.success) {
          setError(responseData.message);
          setIsLoginOk(false);
        }
      }
    }
  }, [isLoginOk, responseData]);

  const sendQrPinCode = async ({ qrPinText }: SignInCredentials) => {
    try {
      setError('');
      const postQrPincodefy = loadify(postQrPincode, setLoading);
      const responseData = await postQrPincodefy(qrPinText);
      throwOnServerError(responseData);
      responseData.success === true && navigateToApp(responseData, navigate);
    } catch (error) {
      setError(error);
    }
  };

  const sendPinCode = async ({ phonePinText }: SignInCredentials) => {
    try {
      setError('');
      const postPhonePincodefy = loadify(postPhonePincode, setLoading);
      const responseData = await postPhonePincodefy(phonePinText);
      throwOnServerError(responseData);
      routeTo(LOGIN_ROUTES.QR_VERIFICATION, responseData, navigate);
    } catch (error) {
      setError(error);
    }
  };

  const sendResetPassword = async ({ email }: SignInCredentials) => {
    try {
      setError('');
      const getResetPasswordRequestfy = loadify(getResetPasswordRequest, setLoading);
      const responseData = await getResetPasswordRequestfy(email ? encodeURIComponent(email) : '');
      throwOnServerError(responseData);
      setSuccess(true);
      return { data: responseData, success: true };
    } catch (error) {
      setError(error);
      setSuccess(false);
      return { error, success: false };
    }
  };

  const sendResetPasswordfy = async ({ email, newPassword, token }: SignInCredentials) => {
    try {
      setError('');
      const postResetPasswordfy = loadify(postResetPassword, setLoading);
      const responseData = await postResetPasswordfy(email, newPassword, token);
      throwOnServerError(responseData);
      setSuccess(true);
    } catch (error) {
      setError(error);
      setSuccess(false);
    }
  };

  const sendContactUser = ({ byPhone }: SignInCredentials) => {
    contactUser(byPhone === 'call');
  };

  const handleSignIn = async ({
    code,
    accessExpirationInMS,
    data,
    sessionId
  }: SignInResponse & { data: string; sessionId?: string }) => {
    switch (code) {
      case SignInResponseCode.Authenticated:
        await navigateToApplication({ accessExpirationInMS, refreshPage: true, sessionId }, navigate);
        break;
      case SignInResponseCode.ValidatePhone:
        sessionStorage.setItem(
          MFA_SESSION_KEYS.PHONE_EMAIL_STATE,
          JSON.stringify({
            fromSignIn: true,
            resendContact: true,
            contactType: code,
            message: '',
            phone: data
          })
        );
        window.location.replace(window.location.origin + LOGIN_ROUTES.PHONE_VERIFICATION);
        break;
      case SignInResponseCode.ValidateEmail:
        sessionStorage.setItem(
          MFA_SESSION_KEYS.PHONE_EMAIL_STATE,
          JSON.stringify({
            fromSignIn: true,
            resendContact: true,
            contactType: code,
            message: '',
            email: data
          })
        );
        window.location.replace(window.location.origin + LOGIN_ROUTES.PHONE_VERIFICATION);
        break;
      case SignInResponseCode.MfaPinCode:
        sessionStorage.setItem(
          MFA_SESSION_KEYS.QR_STATE,
          JSON.stringify({
            is_required: true,
            message: '',
            shouldShowQr: false,
            mfa_qr_code_base64: '',
            mfa_manual_entry_key: ''
          })
        );
        window.location.replace(window.location.origin + LOGIN_ROUTES.QR_VERIFICATION);
        break;
      case SignInResponseCode.PasswordExpired:
        window.location.replace(window.location.origin + LOGIN_ROUTES.PASSWORD_EXPIRED);
        break;
      case SignInResponseCode.SsoRedirect:
        if (data) {
          // use window native navigate as SSORedirectUrl is on an another domain
          window.location.href = data;
        }
        break;
      default:
        break;
    }
  };

  return {
    isLoginOk,
    login,
    sendQrPinCode,
    sendPinCode,
    sendResetPassword,
    sendResetPasswordfy,
    sendContactUser,
    clearError,
    loading,
    error,
    isSuccess
  };
}
